import {
  Alert,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Loading, useGetList } from 'react-admin';
import type { RateRule } from '../dataProvider';
import { resolveEffectiveRate } from './resolveEffectiveRate';
import { scopeMatchesRegion, targetForRule } from './rateRuleScope';
import { useRateRuleCatalog } from './useRateRuleCatalog';
import { formatEffect } from './rateRuleState';
import {
  candidateToRule,
  previewTier,
  type CandidateRule,
} from './candidateRule';
import { formatRate } from '../stores/formatRate';

/**
 * CONFLICT-RESOLUTION PREVIEW (D-rate-rules) — the heart of the surface. For a
 * SAMPLE in-scope store it resolves the store against ALL active rules plus the
 * in-progress rule (treated as active) and shows WHICH rule wins and WHY: the
 * contenders ranked by priority, the winner, and the resulting effective rate.
 * Winner-takes-one, no stacking. A PROJECTION over today's rules, not the live
 * per-click resolution.
 */
export const RuleConflictPreview = ({
  candidate,
  now,
}: {
  candidate: CandidateRule;
  now: number;
}) => {
  const { views, isLoading: catalogLoading } = useRateRuleCatalog();
  const { data: saved = [], isLoading: rulesLoading } = useGetList<RateRule>(
    'rateRules',
    { pagination: { page: 1, perPage: 1000 } },
  );
  if (catalogLoading || rulesLoading) return <Loading />;

  const rule = candidateToRule(candidate);
  const tier = previewTier(candidate);

  // Active saved rules (excluding this one if it is already saved) plus the
  // in-progress candidate, so the operator sees who would win once it is live.
  const activeRules = saved.filter(
    (r) => r.status === 'active' && r.id !== candidate.id,
  );
  const allRules = [...activeRules, rule];

  // The first store the candidate's scope matches, in its market scope.
  const sample = views.find(
    (v) =>
      (rule.market === v.market || rule.market === 'all') &&
      scopeMatchesRegion(rule, v),
  );

  if (!sample) {
    return (
      <Box data-testid="conflict-preview">
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Conflict preview
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No store in scope to resolve yet.
        </Typography>
      </Box>
    );
  }

  const target = targetForRule(rule, sample);
  const res = resolveEffectiveRate(target, allRules, now, tier);

  return (
    <Box data-testid="conflict-preview">
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Conflict preview (sample store #{sample.regionId})
      </Typography>
      <Alert severity="info" sx={{ mb: 1, py: 0 }}>
        A projection over today&apos;s rules for a {tier} member, not the live
        per-click resolution. Winner takes one, rules do not stack.
      </Alert>
      <Typography variant="body2" sx={{ mb: 1 }} data-testid="conflict-result">
        Base {formatRate(res.baseRate)} resolves to{' '}
        <strong>{formatRate(res.effectiveRate)}</strong>
        {res.winner
          ? `, won by "${res.winner.displayLabel ?? res.winner.scopeType}" (priority ${res.winner.priority}).`
          : ', no rule applies.'}
      </Typography>
      <Table size="small" aria-label="contending rules">
        <TableHead>
          <TableRow>
            <TableCell>Rule</TableCell>
            <TableCell>Priority</TableCell>
            <TableCell>Effect</TableCell>
            <TableCell>Outcome</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {res.contenders.map((r, i) => (
            <TableRow
              key={r.id}
              data-testid={`contender-${r.id}`}
              selected={i === 0}
            >
              <TableCell>{r.displayLabel ?? r.scopeType}</TableCell>
              <TableCell>{r.priority}</TableCell>
              <TableCell>{formatEffect(r)}</TableCell>
              <TableCell>
                {i === 0 ? (
                  <Chip
                    size="small"
                    color="success"
                    label="Wins"
                    data-testid="conflict-winner"
                  />
                ) : (
                  <Chip
                    size="small"
                    variant="outlined"
                    label="Does not stack"
                  />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
